import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import apiClient from '../services/apiClient';

// Bundesländer-Mapping
const BUNDESLAENDER = {
  BWB: "Baden-Württemberg",
  FSB: "Bayern",
  BLN: "Berlin",
  BRB: "Brandenburg",
  BRE: "Bremen",
  HAM: "Hamburg",
  HES: "Hessen",
  MVP: "Mecklenburg-Vorpommern",
  NSB: "Niedersachsen",
  NRW: "Nordrhein-Westfalen",
  RPF: "Rheinland-Pfalz",
  SAA: "Saarland",
  FSS: "Sachsen",
  SAN: "Sachsen-Anhalt",
  SHS: "Schleswig-Holstein",
  FST: "Thüringen",
  BRD: "Bundesrepublik Deutschland"
};

// Komponente für eine Bundesland-Gruppe
const BundeslandGroup = ({ bundesland, gerichte }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box sx={{ mb: 1 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          borderRadius: 1,
          p: 0.5
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <IconButton size="small">
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <Typography sx={{ flex: 1 }}>
          {BUNDESLAENDER[bundesland] || bundesland} ({gerichte.length})
        </Typography>
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 4 }}>
          {gerichte.map((gericht) => (
            <Typography 
              key={gericht.id}
              sx={{ 
                py: 0.5,
                color: 'text.secondary',
                fontSize: '0.95rem'
              }}
            >
              • {gericht.bezeichnung}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default function AdminPage() {
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    role: null,
    gerichte: []
  });
  const [stats, setStats] = useState({
    gerichte: 0,
    entscheidungen: 0,
    benutzer: 0
  });

  const fetchUserData = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      const user = response.data;
      
      setUserData({
        username: user.username,
        email: user.email,
        role: user.role,
        gerichte: user.gerichte || []
      });
    } catch (error) {
      console.error('Fehler beim Laden der Benutzerdaten:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchStats();
  }, []);

  // Gruppiere Gerichte nach Bundesland
  const groupedGerichte = userData.gerichte.reduce((acc, gericht) => {
    const bundesland = gericht.bundesland || 'Andere';
    if (!acc[bundesland]) {
      acc[bundesland] = [];
    }
    acc[bundesland].push(gericht);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Admin-Bereich
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Profilinformationen
        </Typography>

        <Grid container spacing={3}>
          {/* Basisinfos */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Benutzerdaten
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Benutzer
                  </Typography>
                  <Typography>
                    <strong>{userData.username}</strong>
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    E-Mail
                  </Typography>
                  <Typography>
                    <strong>{userData.email}</strong>
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Rolle
                  </Typography>
                  <Typography>
                    <strong>{userData.role?.name || 'Keine Rolle zugewiesen'}</strong>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Berechtigungen */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Berechtigungen
              </Typography>
              <Box sx={{ 
                pl: 2,
                borderLeft: '2px solid',
                borderColor: 'primary.main',
                mt: 1
              }}>
                {userData.role?.permissions?.length > 0 ? (
                  userData.role.permissions.map((permission, index) => (
                    <Typography 
                      key={permission.id} 
                      sx={{ 
                        mb: index < userData.role.permissions.length - 1 ? 1 : 0,
                        color: 'text.primary'
                      }}
                    >
                      • {permission.description || permission.name}
                    </Typography>
                  ))
                ) : (
                  <Typography color="text.secondary">
                    Keine Berechtigungen
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Gerichte */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Zugewiesene Gerichte ({userData.gerichte.length})
              </Typography>
              {userData.gerichte.length > 0 ? (
                <Box sx={{ 
                  pl: 2,
                  borderLeft: '2px solid',
                  borderColor: 'primary.main',
                  mt: 1,
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  {Object.entries(groupedGerichte)
                    .sort(([a], [b]) => {
                      const nameA = BUNDESLAENDER[a] || a;
                      const nameB = BUNDESLAENDER[b] || b;
                      return nameA.localeCompare(nameB);
                    })
                    .map(([bundesland, gerichte]) => (
                      <BundeslandGroup 
                        key={bundesland}
                        bundesland={bundesland}
                        gerichte={gerichte}
                      />
                    ))
                  }
                </Box>
              ) : (
                <Typography color="text.secondary">
                  Keine Gerichte zugewiesen
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Statistiken
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Gerichte
              </Typography>
              <Typography variant="h4">
                {stats.gerichte}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Benutzer
              </Typography>
              <Typography variant="h4">
                {stats.benutzer}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Entscheidungen
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: 'success.light',
                    borderRadius: 1,
                    color: 'white'
                  }}>
                    <Typography variant="subtitle2">
                      Aktiv
                    </Typography>
                    <Typography variant="h5">
                      {stats.entscheidungen_aktiv}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: 'error.light',
                    borderRadius: 1,
                    color: 'white'
                  }}>
                    <Typography variant="subtitle2">
                      Deaktiviert
                    </Typography>
                    <Typography variant="h5">
                      {stats.entscheidungen_inaktiv}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: 'info.light',
                    borderRadius: 1,
                    color: 'white'
                  }}>
                    <Typography variant="subtitle2">
                      Gesamt
                    </Typography>
                    <Typography variant="h5">
                      {stats.entscheidungen_gesamt}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
} 